/**
 * Every user-visible string in the consent banner, in one place.
 *
 * ## Why this module exists
 *
 * The wording is legal-adjacent: it is the disclosure a CIPA/CPRA claim would
 * be argued against. Keeping it here means the person with the spec can change
 * what the banner SAYS without touching how it behaves, and means a copy edit
 * can be reviewed as copy rather than as a diff to a React component.
 *
 * **If you are the person with the spec: change the values below and nothing
 * else.** The banner reads every string from this object; none are inlined in
 * the component.
 *
 * Two constraints worth keeping when the copy changes again:
 *   - `bodyLead` + `bodyTail` wrap the inline privacy link, so the sentence
 *     reads continuously. Keep the link mid-sentence or restructure both.
 *   - `accept` and `decline` should stay similar in length. They share
 *     geometry, and a much longer label on one visually weights that choice —
 *     the symmetry-in-choice point in CPRA §7004(a)(2).
 *
 * ## Changing the wording means bumping CONSENT_VERSION
 *
 * A receipt records assent to a specific disclosure. If the disclosure
 * materially changes, previously-stored receipts no longer evidence agreement
 * to what the banner now says, so `CONSENT_VERSION` in `consentPolicy.ts` (and
 * its mirror in `public/index.html`) must move with it. That re-prompts
 * everyone, which is the point.
 */
export const CONSENT_COPY = {
  /** Accessible name of the banner region. Not rendered visually. */
  regionLabel: 'Your privacy choices',
  /** Sentence before the inline privacy-policy link. */
  bodyLead:
    'Thea and our partners use cookies and similar technologies to show you relevant ads and improve our site. See our ',
  /** The inline link text. */
  privacyLinkText: 'Privacy Policy',
  /** Sentence after the link. */
  bodyTail: '.',
  /** Primary choice. */
  accept: 'Accept',
  /** Equal-weight alternative. */
  decline: 'Decline',
  /** Accessible name for the X. Not rendered visually. */
  dismiss: 'Dismiss for now',
} as const;

/** Where the inline link points. */
export const PRIVACY_HREF = '/privacy';
